// EU AI Act (Art. 50): JS-Positionierer des KI-Kennzeichnungs-Badges.
//
// Der Badge haftet unten rechts über dem Medium und bleibt beim Scrollen im
// SICHTBAREN Ausschnitt: er sitzt 10px innerhalb der Clip-Box (= Media-Box
// geschnitten mit allen clippenden Vorfahren + Viewport), unten wie rechts, und
// wandert – läuft das Medium oben aus – als 10px-Streifen an der oberen Kante mit
// heraus (statt bündig am Rand zu kleben, was CSS-sticky nicht clampen kann).
//
// Warum JS statt CSS `position: sticky`?
//  1. Swiper-Slider: Slide-Wrapper haben transform + overflow → der Sticky-Bezug
//     ist nicht mehr der Viewport; CSS-sticky versagt. Auch collabierte Material-
//     Slides zeigen nur einen schmalen `.swiper-material-wrapper` – die Clip-Box
//     hält den Badge an dessen sichtbarer Kante, nicht an der vollen Bildbreite.
//  2. Full-bleed/große Medien: läuft das Medium oben aus dem Bild, kann CSS-sticky
//     den 10px-Abstand zur OBERkante nicht halten (es klebt bündig). Der Clamp hier
//     hält ihn konsistent 10px innen.
//
// Bewusst NICHT hier (bleiben auf nativem CSS-sticky, wo es robuster ist):
//  - Stages (scrollStage/stage/stageTreatment): GSAP-Pin bzw. Parallax
//    transformieren das Medium pro Frame → JS läuft dem Transform 1 Frame
//    hinterher und ruckelt („springt"). CSS-sticky bleibt dort flüssig.
//  - Finder/Hotspots: Vue-Island-Galerien mit absolut überlagerten, cross-fadenden
//    Medien und eigenem Badge-Slot-Overlay – dort emuliert das bespoke Markup die
//    Haftung bereits, JS brächte kaum Gewinn bei Interaktions-Risiko.

const PAD = 10; // = var(--size-10)
// Alle geradlinigen Medien-Overlay-Badges: Slider-Slides, die Grid-Overlay-Medien
// (.media--ai-labeled) quer durch alle Sektionen, sowie die section-weiten Badges,
// die als eigenes Grid-Item/Overlay über ihrer Medienzelle liegen. Die in diesen
// Sektionen ausgeblendeten inneren `.media--ai-labeled > .media__ai-label`
// (display:none) werden in update() automatisch übersprungen (offsetParent null).
// Stage-/Finder-/Hotspots-Badges matchen bewusst KEINEN Eintrag (siehe oben).
const SELECTOR = [
    ".swiper-slide .media__ai-label",
    "swiper-slide .media__ai-label",
    ".media--ai-labeled > .media__ai-label",
    ".section__ai-label",
    ".section-text-on-video__ai-label",
    ".section-text-on-image__ai-label",
    ".section-image-on-text__ai-label",
    ".section-text-media__ai-label",
].join(", ");

// Die Box, an deren SICHTBAREM Ausschnitt der Badge klebt (nicht zwingend der
// offsetParent – s. u.). Reihenfolge:
//  1. Grid-Overlay-Badge liegt IN der `.media--ai-labeled` → diese Box.
//  2. Section-weiter Badge liegt als Grid-Item/Overlay über seiner Medienzelle,
//     das Medium ist sein Geschwister → das Medium im selben Eltern-Container.
//  3. text-media: der Badge ist ein Item des SECTION-Grids (nicht in der
//     Medienzelle) und wird nur per grid-column in die Medienspalte gelegt →
//     das Medium liegt in `.section-text-media__media` derselben Section.
//  4. Fallback (u. a. Section-Hintergrund `.section__ai-label`, dessen Layer die
//     ganze Section abdeckt, weil das BG-Bild sie füllt): der offsetParent selbst.
function mediaBoxFor(label) {
    const wrap = label.closest(".media--ai-labeled");
    if (wrap) return wrap;

    const parent = label.parentElement;
    if (parent) {
        const sib = parent.querySelector(".media--ai-labeled, .media");
        if (sib && sib !== label) return sib;
    }

    if (label.classList.contains("section-text-media__ai-label")) {
        const sec = label.closest(".section-text-media");
        const cell = sec && (
            sec.querySelector(".section-text-media__media .media--ai-labeled") ||
            sec.querySelector(".section-text-media__media")
        );
        if (cell) return cell;
    }

    return label.offsetParent;
}

let ticking = false;
let started = false;
let looping = false;
let loopUntil = 0;

// Sichtbare Clip-Box eines Elements: sein Rect, geschnitten mit den Rects aller
// Vorfahren, die auf der jeweiligen Achse clippen (overflow ≠ visible), plus
// Viewport. Material-Slides clippen horizontal über den schmalen Wrapper.
function visibleClipRect(el, vh, vw) {
    const r = el.getBoundingClientRect();
    let left = r.left, top = r.top, right = r.right, bottom = r.bottom;
    let a = el.parentElement;
    while (a && a !== document.body && a !== document.documentElement) {
        const cs = getComputedStyle(a);
        if (cs.overflowX !== "visible" || cs.overflowY !== "visible") {
            const ar = a.getBoundingClientRect();
            if (cs.overflowX !== "visible") { left = Math.max(left, ar.left); right = Math.min(right, ar.right); }
            if (cs.overflowY !== "visible") { top = Math.max(top, ar.top); bottom = Math.min(bottom, ar.bottom); }
        }
        a = a.parentElement;
    }
    // Viewport
    top = Math.max(top, 0);
    left = Math.max(left, 0);
    bottom = Math.min(bottom, vh);
    right = Math.min(right, vw);
    return { left, top, right, bottom };
}

function update() {
    ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;

    // Zwei Pässe: erst ALLE Positionen lesen, dann ALLE schreiben. Sonst würde das
    // abwechselnde Lesen (getBoundingClientRect) + Schreiben (style) je Badge einen
    // erzwungenen Reflow auslösen – bei synchronen Aufrufen während des Material-
    // Effekts (jeder Frame) führte das zu Ruckeln.
    const writes = [];
    document.querySelectorAll(SELECTOR).forEach((label) => {
        // Umgebende Swiper lazy beobachten (kann nach dem init-Aufruf mounten,
        // z. B. als Vue-Island) → beim Slide-Wechsel/Drag/Autoplay neu positionieren.
        observeSwiper(label.closest(".swiper, swiper-container"));

        // Containing Block = der Bezug für die absoluten Insets (offsetParent).
        // Die MEDIA-Box (an deren sichtbarem Ausschnitt geclampt wird) kann eine
        // ANDERE Box sein (z. B. bei text-media liegt der Badge im Section-Grid,
        // das Medium aber in seiner eigenen Spalte). Beides trennen: Position gegen
        // den Containing Block schreiben, aber gegen den Clip der Media-Box clampen.
        const cb = label.offsetParent;
        if (!cb) return; // display:none (verstecktes/inneres Badge) → überspringen
        const cbr = cb.getBoundingClientRect();

        const media = mediaBoxFor(label) || cb;
        const mr = media.getBoundingClientRect();
        if (mr.height <= 0 || mr.width <= 0) return;
        const lh = label.offsetHeight || 0; // inkl. padding-block-end (= 10px-Abstand)
        const clip = visibleClipRect(media, vh, vw);
        if (clip.right - clip.left <= 0 || clip.bottom - clip.top <= 0) return; // nicht sichtbar

        // Der Span wird BÜNDIG an die rechte/untere Kante der sichtbaren Clip-Box
        // gesetzt; der 10px-Abstand kommt aus dem CSS-padding des Badges (svg sitzt
        // dadurch 10px innerhalb) – identisch zum CSS-sticky-Fall, kein Doppel.
        // Horizontal: rechte Span-Kante an die rechte Clip-Kante (collabierte
        // Material-Slides / links stehende Medien = schmaler als der Containing
        // Block). inset-inline-end misst vom rechten Rand des Containing Blocks.
        const insetIE = Math.max(0, Math.round(cbr.right - clip.right));
        // Vertikal: Span-Unterkante an die untere Clip-Kante (svg 10px darüber via
        // padding). Läuft das Medium oben zu einem schmalen Streifen aus, den Span
        // 10px unter der sichtbaren OBERkante halten (svg sitzt am Span-Top) →
        // 10px von oben statt bündig. Die Padding-Unterkante darf dabei unsichtbar
        // unter den Ausschnitt ragen. `top` relativ zur Containing-Block-Oberkante.
        let topVp = Math.max(clip.bottom - lh, clip.top + PAD);

        writes.push({ label, insetIE, top: Math.round(topVp - cbr.top) });
    });

    writes.forEach(({ label, insetIE, top }) => {
        // Absolut erzwingen (einmalig, inline → schlägt das CSS-sticky, das im
        // Slider ohnehin nicht greift).
        if (label.dataset.aiPositioned !== "1") {
            label.style.position = "absolute";
            label.style.insetBlockStart = "auto";
            label.style.insetInlineStart = "auto";
            label.style.marginBlockEnd = "0";
            label.style.marginInlineEnd = "0";
            // Grid-Platzierung neutralisieren: Für ein ABSOLUT positioniertes
            // Grid-Item ist der Containing Block seine GRID-AREA, nicht die
            // Padding-Box des offsetParent. Bei einer Teil-Platzierung (text-media:
            // Badge in Spalte 1–6) läge der Bezug so bei der halben Section und die
            // gegen den offsetParent gerechneten Insets schössen daneben. Mit
            // grid-area:auto ist der Containing Block wieder die Padding-Box des
            // offsetParent (= die Box, deren Rect cbr misst) – für alle Fälle korrekt.
            label.style.gridArea = "auto";
            label.dataset.aiPositioned = "1";
        }
        label.style.insetInlineEnd = `${insetIE}px`;
        label.style.top = `${top}px`;
        label.style.insetBlockEnd = "auto";
    });
}

function onScroll() {
    if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
    }
}

// Der Material-Effekt animiert die Slides per CSS-Transition (Compositor) – es gibt
// KEINE Style-Mutation pro Frame, die MutationObserver feuert nur am Start/Ende.
// Um die Badge während der Transition mitzuführen (statt am Ende zu snappen), läuft
// eine rAF-Schleife für die Transitionsdauer: jeder Frame liest die aktuelle
// (animierte) Position und positioniert neu. Jede neue Änderung verlängert das Fenster.
function runLoop() {
    update();
    if (performance.now() < loopUntil) {
        requestAnimationFrame(runLoop);
    } else {
        looping = false;
    }
}

function kickLoop() {
    loopUntil = performance.now() + 600; // deckt die ~500ms-Slide-Transition + Puffer
    if (!looping) {
        looping = true;
        requestAnimationFrame(runLoop);
    }
}

// Der Material-Effekt (und Swiper allgemein) transformiert Slides/Content beim
// Wechsel/Drag/Autoplay per inline style + Klassen – OHNE Scroll-Event. Ohne
// Reaktion bliebe die Badge an alter Position. Darum die umgebende Swiper pro
// style/class-Änderung neu positionieren. Badge-eigene Style-Änderungen (die
// update() selbst setzt) werden ignoriert, sonst Endlosschleife.
function observeSwiper(sw) {
    if (!sw || sw.dataset.aiObserved === "1") return;
    sw.dataset.aiObserved = "1";
    new MutationObserver((mutations) => {
        for (const m of mutations) {
            const t = m.target;
            if (t.classList && t.classList.contains("media__ai-label")) continue;
            // Start/Ende einer Transition (bzw. Drag-Frame) → rAF-Schleife anwerfen,
            // die die Badge über die CSS-Transition hinweg mitführt. Badge-eigene
            // Style-Änderungen sind gefiltert (sonst Dauerlauf).
            kickLoop();
            return;
        }
    }).observe(sw, { attributes: true, attributeFilter: ["style", "class"], subtree: true });
}

export function initAiLabelSticky() {
    if (!started) {
        started = true;
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        window.addEventListener("load", onScroll, { passive: true, once: true });
    }
    update();
    // Slider (Vue-Islands) mounten teils NACH diesem Aufruf → Nachläufe fangen sie
    // ab; update() beobachtet dann lazy jede Swiper, die eine Badge enthält.
    [200, 800, 2000].forEach((d) => setTimeout(onScroll, d));
}
